/**
 * Vorlage „Schälen“.
 *
 * Draufsicht: Ein Sparschäler zieht Bahn für Bahn über die Zutat. Wo er war,
 * kommt das helle Innere zum Vorschein; die Schalenstreifen fallen zur Seite.
 */
import { ease, fract, lerp, random, segment } from '../geometry.ts';
import { STROKE } from '../palette.ts';
import type { Tone } from '../palette.ts';
import { path, roundedRectPath } from '../paths.ts';
import { compact, faded, group, line, rect, rotated, shape, styles } from '../scene.ts';
import type { SceneNode } from '../scene.ts';
import { board } from '../parts/knife.ts';
import { produce, produceNodes } from '../parts/produce.ts';
import type { ProduceKind } from '../parts/produce.ts';
import { arrow } from '../parts/stage.ts';
import { choice, defineTemplate, textValue } from '../template.ts';

/** Lage der Zutat. */
const BOX = { x: 40, y: 40, width: 120, height: 54 } as const;
/** Zahl der Bahnen. */
const ROWS = 4;

/** Zeitplan. */
const TIME = { appear: [0, 0.05], peel: [0.06, 0.82], vanish: [0.93, 1] } as const;

/** Zutaten und die Farbe des geschälten Inneren. */
const ITEMS = { potato: 'Kartoffel', carrot: 'Möhre', cucumber: 'Gurke' } as const;
const FLESH: Readonly<Record<keyof typeof ITEMS, Tone>> = {
  potato: 'paper',
  carrot: 'tint',
  cucumber: 'tint',
};

/** Sparschäler in der Draufsicht: Klinge senkrecht im Ursprung, Griff nach rechts. */
function peeler(): SceneNode[] {
  return [
    shape(path().move(0, -12).line(14, -3).line(14, 3).line(0, 12).toString(), {
      stroke: 'ink',
      strokeWidth: STROKE.outline,
    }),
    shape(roundedRectPath(12, -5, 46, 10, 5), styles.solid('ink')),
    rect(-3, -13, 6, 26, 2, styles.solid('paper')),
    line(0, -10, 0, 10, styles.fine('deep')),
  ];
}

/** Schalenstreifen, eingerollt. */
function curl(x: number, y: number, turn: number, tone: Tone): SceneNode {
  const d = path()
    .move(x - 10, y)
    .quad(x - 4, y - 7, x + 4, y - 3)
    .quad(x + 10, y + 1, x + 6, y + 5)
    .quad(x, y + 6, x - 10, y)
    .close()
    .toString();
  return rotated(turn, x, y, [shape(d, { fill: tone, stroke: 'ink', strokeWidth: STROKE.detail })]);
}

/** Vorlage „Schälen“. */
export const cutPeelTemplate = defineTemplate({
  id: 'cut.peel',
  group: 'cut',
  title: 'Schälen',
  description:
    'Mit dem Sparschäler wird die Schale Bahn für Bahn dünn abgezogen, bis die Zutat rundum geschält ist.',
  durationSeconds: 6,
  keywords: ['schälen', 'geschält', 'sparschäler', 'pellen', 'schale entfernen'],
  stillTime: 0.45,
  parameters: [choice('ingredient', 'Zutat', ITEMS, 'potato')],
  inferValues(text) {
    const lower = text.toLowerCase();
    if (lower.includes('kartoffel')) return { ingredient: 'potato' };
    if (/möhre|karotte/.test(lower)) return { ingredient: 'carrot' };
    if (lower.includes('gurke')) return { ingredient: 'cucumber' };
    return {};
  },
  scene(values, t) {
    const kind = textValue(values, 'ingredient', 'potato') as ProduceKind;
    const item = produce(kind, BOX);
    const flesh = FLESH[kind as keyof typeof ITEMS] ?? 'paper';
    const visible = Math.min(
      ease.outCubic(segment(t, ...TIME.appear)),
      1 - ease.inCubic(segment(t, ...TIME.vanish)),
    );
    const peel = segment(t, ...TIME.peel);
    const rowHeight = BOX.height / ROWS;

    // Geschälte Bahnen: helle Streifen innerhalb des Umrisses.
    const bands: SceneNode[] = [];
    const curls: SceneNode[] = [];
    for (let row = 0; row < ROWS; row += 1) {
      const u = segment(peel, row / ROWS, (row + 0.8) / ROWS);
      if (u <= 0) {
        continue;
      }
      const y = BOX.y + row * rowHeight - (row === 0 ? 6 : 0);
      const height = rowHeight + (row === 0 || row === ROWS - 1 ? 6 : 0) + 0.5;
      const right = lerp(BOX.x - 4, BOX.x + BOX.width + 4, ease.inOutSine(u));
      bands.push(rect(BOX.x - 6, y, right - BOX.x + 6, height, 0, styles.flat(flesh)));
      if (row > 0) {
        bands.push(line(BOX.x - 6, y, right, y, styles.fine('shade')));
      }
      if (u >= 1) {
        const rng = random(row * 7 + 2);
        curls.push(curl(176 + rng() * 34, 106 + row * 6 + rng() * 6, rng() * 360, item.fill));
      }
    }
    const peeled = group([
      ...produceNodes(item).slice(0, -1),
      group(bands, { clip: item.outline }),
      produceNodes(item).at(-1) ?? null,
    ]);

    // Schäler: zieht von links nach rechts, dann leicht angehoben zurück.
    const local = fract(peel * ROWS);
    const row = Math.min(ROWS - 1, Math.floor(peel * ROWS));
    const stroke = segment(local, 0, 0.8);
    const back = segment(local, 0.8, 1);
    const x = lerp(BOX.x - 4, BOX.x + BOX.width + 4, ease.inOutSine(stroke)) - back * (BOX.width + 8);
    const y = BOX.y + (row + 0.5) * rowHeight;
    const lifted = back > 0 ? Math.sin(back * Math.PI) : 0;
    const active = peel > 0 && peel < 1;
    const peelerNode = group(peeler(), {
      transform: {
        x: active ? x : BOX.x + BOX.width + 22,
        y: active ? y : 118,
        scale: 1 + 0.08 * lifted,
        rotate: active ? 0 : -20,
        ox: 0,
        oy: 0,
      },
    });
    return compact([...board(), faded(visible, [peeled, ...curls]), peelerNode]);
  },
  hints() {
    return [
      arrow([
        { x: BOX.x, y: BOX.y - 12 },
        { x: BOX.x + BOX.width, y: BOX.y - 12 },
      ]),
    ];
  },
});
